// ─── Service Budget (Firestore) — postes et opérations ────────────
// Modèle : cf. MODELE-DONNEES.md § Budget & finances.
//   budgets/{id}  : un POSTE (nom, emoji, payeurs %, accesN + miroir `acces`)
//   depenses/{id} : une OPÉRATION (nom de collection historique, l'UI dit
//                   « opération » ; dépense OU revenu selon `sens`)
// Les deux sont rattachés à `espaceId` (canon suite) ; `calendrierId` est
// conservé quand il existe — MoPCal continue de l'utiliser.
//
// ⚠️ REQUÊTES : les règles prouvent l'accès par `espaceId` / `calendrierId`.
// Une requête `where budgetId == …` n'est PAS prouvable et échouerait
// ENTIÈREMENT (piège connu, cf. COWORK). On requête donc TOUJOURS par espace,
// puis on filtre par budget côté client.
import { journaliser } from '@mop/core';
import { db } from '@mop/core';
import { accesUidsDe, accesTousDe } from '@mop/core';
import {
  collection, addDoc, updateDoc, deleteDoc, doc, getDoc, getDocs,
  onSnapshot, query, where, writeBatch,
} from 'firebase/firestore';
import { normaliserPayeurs } from './calculs';

// Miroirs requêtables du modèle d'accès (budgets = mêmes noms que
// inventaires/plans : tableau `acces`, matrice `accesN`).
const miroirs = (accesN) => ({ acces: accesUidsDe(accesN), accesTous: accesTousDe(accesN) });

// Trace d'une opération : best-effort, jamais bloquant (cf. Core/historique).
// Sans elle, une disparition de dépenses ne laisse AUCUN moyen de savoir quand
// ni comment — c'est ce qui s'est produit sur deux ans d'import bancaire.
const tracer = (op, action, auteur) => journaliser(
  { calId: op?.calendrierId || null, espaceId: op?.espaceId || null },
  {
    type: 'operation', action,
    titre: (op?.libelle || '').trim(),
    dateConcernee: op?.date || null,
    montant: Number(op?.montant) || 0,
    detail: op?.sens === 'revenu' ? 'revenu' : 'depense',
    par: auteur?.uid || op?.creePar || null, parNom: auteur?.nom || '',
  });

// ── Postes ───────────────────────────────────────────────────────
export function ecouterBudgets(espaceId, callback, onErreur) {
  if (!espaceId) { callback([]); return () => {}; }
  const q = query(collection(db, 'budgets'), where('espaceId', '==', espaceId));
  return onSnapshot(q, (snap) => {
    const l = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    l.sort((a, b) => (a.nom || '').localeCompare(b.nom || ''));
    callback(l);
  }, (e) => { callback([]); onErreur?.(e); });
}

export async function creerBudget({ espaceId, calendrierId, nom, emoji, accesN, payeurs, creePar }) {
  const a = accesN || {};
  const ref = await addDoc(collection(db, 'budgets'), {
    espaceId,
    ...(calendrierId ? { calendrierId } : {}),
    nom: (nom || '').trim() || 'Budget',
    emoji: emoji || '💰',
    accesN: a, ...miroirs(a),
    payeurs: normaliserPayeurs(payeurs),
    creePar: creePar || null,
    creeLe: new Date().toISOString(),
  });
  return ref.id;
}

export async function modifierBudget(id, { nom, emoji, accesN, payeurs }) {
  const a = accesN || {};
  await updateDoc(doc(db, 'budgets', id), {
    nom: (nom || '').trim() || 'Budget',
    emoji: emoji || '💰',
    accesN: a, ...miroirs(a),
    payeurs: normaliserPayeurs(payeurs),
  });
}

// Supprime le poste ET ses opérations. Un poste SYSTÈME (budget de transit de
// l'import bancaire) n'est jamais supprimable.
export async function supprimerBudget(id, espaceId) {
  const b = await getDoc(doc(db, 'budgets', id));
  if (b.exists() && b.data().systeme) throw new Error('Budget système non supprimable');
  const snap = await getDocs(query(collection(db, 'depenses'), where('espaceId', '==', espaceId)));
  const refs = snap.docs.filter(d => d.data().budgetId === id).map(d => d.ref);
  refs.push(doc(db, 'budgets', id));
  const LOT = 450;
  for (let i = 0; i < refs.length; i += LOT) {
    const batch = writeBatch(db);
    refs.slice(i, i + LOT).forEach(r => batch.delete(r));
    await batch.commit();
  }
}

// ── Opérations ───────────────────────────────────────────────────
export function ecouterOperations(espaceId, callback, onErreur) {
  if (!espaceId) { callback([]); return () => {}; }
  const q = query(collection(db, 'depenses'), where('espaceId', '==', espaceId));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  }, (e) => { callback([]); onErreur?.(e); });
}

const montantValide = (montant) => {
  const m = Math.round((parseFloat(String(montant).replace(',', '.')) || 0) * 100) / 100;
  if (!m || m <= 0) throw new Error('Montant invalide');
  return m;
};

export async function ajouterOperation({
  espaceId, calendrierId, budgetId, montant, payeurUid, payeurNom, categorie, libelle,
  date, datePaiement, typePaiement, commentaire, sens, paye, creePar,
}) {
  const _op = {
    espaceId,
    ...(calendrierId ? { calendrierId } : {}),
    budgetId: budgetId || null,
    recurrenceId: null,
    paye: paye === false ? false : true,          // false = provision
    sens: sens === 'revenu' ? 'revenu' : 'depense',
    montant: montantValide(montant),
    payeurUid: payeurUid || null,
    payeurNom: payeurNom || '',
    categorie: categorie || 'autre',
    libelle: (libelle || '').trim(),
    date: date || new Date().toISOString().slice(0, 10),
    datePaiement: (datePaiement || '').trim(),
    typePaiement: typePaiement || '',
    commentaire: (commentaire || '').trim(),
    pieces: [],
    creePar: creePar || null,
    creeLe: new Date().toISOString(),
  };
  await addDoc(collection(db, 'depenses'), _op);
  tracer(_op, 'creation');
}

// Modification partielle : seuls les champs FOURNIS sont écrits (une opération
// importée garde ses infos banque, qu'on ne touche jamais ici).
export async function modifierOperation(id, champs, op) {
  const out = {};
  const copier = (k, f) => { if (champs[k] !== undefined) out[k] = f ? f(champs[k]) : champs[k]; };
  copier('montant', montantValide);
  copier('budgetId', v => v || null);
  copier('payeurUid', v => v || null);
  copier('payeurNom', v => v || '');
  copier('categorie', v => v || 'autre');
  copier('libelle', v => (v || '').trim());
  copier('date');
  copier('datePaiement', v => (v || '').trim());
  copier('typePaiement', v => v || '');
  copier('commentaire', v => (v || '').trim());
  copier('sens', v => (v === 'revenu' ? 'revenu' : 'depense'));
  copier('paye', v => v !== false);
  copier('exclu');
  if (Object.keys(out).length) await updateDoc(doc(db, 'depenses', id), out);
  if (op) tracer({ ...op, ...out }, 'modification');
}

// `op` (facultatif) : l'opération telle qu'affichée, pour la TRACER avant sa
// disparition. Sans elle la suppression reste muette — et c'est précisément ce
// silence qui a rendu impossible de comprendre où étaient passées les dépenses.
export const supprimerOperation = async (id, op) => {
  if (op) tracer(op, 'suppression');
  await deleteDoc(doc(db, 'depenses', id));
};

// Bascule « hors calcul » : l'opération reste visible mais ne compte dans aucun
// total (virement interne, régularisation…).
export const basculerExclusion = (op) => modifierOperation(op.id, { exclu: !op.exclu });

// ── Remboursement ────────────────────────────────────────────────
// C'est une OPÉRATION (`type:'remboursement'`, `exclu:true`) et non une
// collection à part : elle rééquilibre le « qui doit à qui » sans peser dans
// les totaux. Créée en PROVISION (⏳) : elle ne compte qu'une fois confirmée.
export async function creerRemboursement({
  espaceId, calendrierId, budgetId, deUid, deNom, aUid, aNom, montant, date, paye = false, creePar,
}) {
  await addDoc(collection(db, 'depenses'), {
    espaceId,
    ...(calendrierId ? { calendrierId } : {}),
    budgetId: budgetId || null,
    type: 'remboursement',
    exclu: true,
    deUid: deUid || null, deNom: deNom || '',
    aUid: aUid || null, aNom: aNom || '',
    payeurUid: deUid || null, payeurNom: deNom || '',
    categorie: 'remboursement',
    sens: 'depense',
    montant: montantValide(montant),
    libelle: '',
    date: date || new Date().toISOString().slice(0, 10),
    datePaiement: '', typePaiement: '', commentaire: '', pieces: [],
    paye: !!paye,
    creePar: creePar || null,
    creeLe: new Date().toISOString(),
  });
}
